/*
 * Copyright (c) 2019
 *    Reliant Business Valuation, LLC - http://reliantvalue.com
 *    Author: Kirill Vizniuk - kirill@internalvaluation.com
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import {CorporateEntity, Report, RestResponse} from 'src/app/model';
import { environment } from 'src/environments/environment';
import { ApiHeaders } from '../headers';
import { BVQReport } from 'src/app/model/bvq';
import { CustomQuestion } from 'src/app/model/bvq/custom.question';
import { catchError } from 'rxjs/operators';

@Injectable()
export class BVQReportProvider {

  constructor(
    private http: HttpClient
  ) { }

  getReportById(id: string): Observable<RestResponse> {
    return this.http
      .get<RestResponse>(`${environment.API_URL}/api/bvq/report/${id}`, ApiHeaders.getOptions());
  }

  createReport(id: string): Observable<RestResponse> {
    return this.http
      .post<RestResponse>(`${environment.API_URL}/api/bvq/report/${id}/create`, null, ApiHeaders.getOptions());
  }

  saveReport(report: BVQReport, type: string, note: string): Observable<RestResponse> {
    return this.http
      .post<RestResponse>(`${environment.API_URL}/api/bvq/report/${report.ID}?source=${type}&note=${note}`,
        report, ApiHeaders.getOptions());
  }

  saveReportFieldSource(report: BVQReport, field: string, type: string, note: string, sourceTitle: string): Observable<RestResponse> {
    return this.http
      .post<RestResponse>(`${environment.API_URL}/api/bvq/report/${report.ID}/source?field=${field}&source=${type}`, {
          NOTE: note,
          SOURCE_TITLE: sourceTitle
        }, ApiHeaders.getOptions());
  }

  clearReportFieldSource(report: BVQReport, field: string): Observable<RestResponse> {
    return this.http
      .delete<RestResponse>(`${environment.API_URL}/api/bvq/report/${report.ID}/source?field=${field}`, ApiHeaders.getOptions());
  }

  setAsk(report: BVQReport, field: string, value: boolean): Observable<RestResponse> {
    return this.http
      .post<RestResponse>(`${environment.API_URL}/api/bvq/report/${report.ID}/ask?field=${field}&value=${value}`,
        null, ApiHeaders.getOptions());
  }

  setResend(report: BVQReport, field: string, value: boolean): Observable<RestResponse> {
    return this.http
      .post<RestResponse>(`${environment.API_URL}/api/bvq/report/${report.ID}/resend?field=${field}&value=${value}`,
        null, ApiHeaders.getOptions());
  }

  /** Owner Manipulation */
  ownerAdd(report: BVQReport, entity: CorporateEntity): Observable<RestResponse> {
    return this.http
      .post<RestResponse>(`${environment.API_URL}/api/report/${report.ID}/entity/${entity.ID}/owner`, null, ApiHeaders.getOptions());
  }

  ownerDelete(report: BVQReport, entity: CorporateEntity, ownerId: string): Observable<RestResponse> {
    return this.http
      .delete<RestResponse>(`${environment.API_URL}/api/report/${report.ID}/entity/${entity.ID}/owner/${ownerId}`, ApiHeaders.getOptions());
  }

  /** Contact Manipulation */
  contactAdd(report: BVQReport): Observable<RestResponse> {
    return this.http
      .post<RestResponse>(`${environment.API_URL}/api/bvq/report/${report.ID}/contact`, null, ApiHeaders.getOptions());
  }

  contactDelete(report: BVQReport, contactId: string): Observable<RestResponse> {
    return this.http
      .post<RestResponse>(`${environment.API_URL}/api/bvq/report/${report.ID}/contact/${contactId}`,
        null, ApiHeaders.getOptions());
  }

  /** Location Manipulation */
  locationAdd(report: BVQReport): Observable<RestResponse> {
    return this.http
      .post<RestResponse>(`${environment.API_URL}/api/bvq/report/${report.ID}/location`, null, ApiHeaders.getOptions());
  }

  locationDelete(report: BVQReport, locationId: string): any {
    return this.http
      .post<RestResponse>(`${environment.API_URL}/api/bvq/report/${report.ID}/location/${locationId}`,
        null, ApiHeaders.getOptions());
  }

  locationClear(report: BVQReport): Observable<RestResponse> {
    return this.http
      .delete<RestResponse>(`${environment.API_URL}/api/bvq/report/${report.ID}/location/clear`, ApiHeaders.getOptions());
  }

  /** Product Manipulation */
  productAdd(report: BVQReport): Observable<RestResponse> {
    return this.http
      .post<RestResponse>(`${environment.API_URL}/api/bvq/report/${report.ID}/product`, null, ApiHeaders.getOptions());
  }

  productDelete(report: BVQReport, productId: string): Observable<RestResponse> {
    return this.http
      .post<RestResponse>(`${environment.API_URL}/api/bvq/report/${report.ID}/product/${productId}`,
        null, ApiHeaders.getOptions());
  }

  /** Customer Manipulation */
  customerAdd(report: BVQReport): Observable<RestResponse> {
    return this.http
      .post<RestResponse>(`${environment.API_URL}/api/bvq/report/${report.ID}/customer`, null, ApiHeaders.getOptions());
  }

  customerDelete(report: BVQReport, customerId: string): any {
    return this.http
      .post<RestResponse>(`${environment.API_URL}/api/bvq/report/${report.ID}/customer/${customerId}`,
        null, ApiHeaders.getOptions());
  }

  /** Competitor Manipulation */
  competitorAdd(report: BVQReport): Observable<RestResponse> {
    return this.http
      .post<RestResponse>(`${environment.API_URL}/api/bvq/report/${report.ID}/competitor`, null, ApiHeaders.getOptions());
  }

  competitorDelete(report: BVQReport, competitorId: string): any {
    return this.http
      .post<RestResponse>(`${environment.API_URL}/api/bvq/report/${report.ID}/competitor/${competitorId}`,
        null, ApiHeaders.getOptions());
  }

  /** Custom Manipulation */
  customUpdate(reportId: string, custom: CustomQuestion): Observable<RestResponse> {
    return this.http
      .post<RestResponse>(`${environment.API_URL}/api/bvq/report/${reportId}/custom`, custom, ApiHeaders.getOptions());
  }

  customDelete(reportId: string, customId: number): Observable<RestResponse> {
    return this.http
      .post<RestResponse>(`${environment.API_URL}/api/bvq/report/${reportId}/custom/${customId}`, null, ApiHeaders.getOptions());
  }

  /** Analysis Manipulation */
  analysisUpdate(reportId: string, custom: CustomQuestion): Observable<RestResponse> {
    return this.http
      .post<RestResponse>(`${environment.API_URL}/api/bvq/report/${reportId}/analysis`, custom, ApiHeaders.getOptions());
  }

  analysisDelete(reportId: string, customId: number): Observable<RestResponse> {
    return this.http
      .post<RestResponse>(`${environment.API_URL}/api/bvq/report/${reportId}/analysis/${customId}`, null, ApiHeaders.getOptions());
  }

  /** Naics Custom */
  updateNaics(reportId: string, naics: any, type: string, note: string): Observable<RestResponse> {
    return this.http
      .post<RestResponse>(`${environment.API_URL}/api/bvq/report/${reportId}/naics?source=${type}&note=` +
        (note ? note : ''), {
        FIELD: 'NAICS',
        VALUE: naics,
      }, ApiHeaders.getOptions());
  }

  /**Save Report Field */
  save(id: string, field: string, value: any): Observable<RestResponse> {
    return this.http
      .post<RestResponse>(`${environment.API_URL}/api/pma/report/${id}/field`, {
        FIELD: field,
        VALUE: value
      }, ApiHeaders.getOptions());
  }

  /**Save/Update Corporate Entity Field */
  saveCorporateEntityFieldSource(report: BVQReport, field: string, type: string, note: string, eid: String, column: string, value: any, sourceTitle: string): Observable<RestResponse> {
    return this.http
      .post<RestResponse>(`${environment.API_URL}/api/bvq/report/${report.ID}/entity/${eid}/field?field=${field}&source=${type}&note=` +
      (note ? note : '')  + '&sourceTitle=' + (sourceTitle ? sourceTitle : ''), {
        FIELD: column,
        VALUE: value
      }, ApiHeaders.getOptions());
  }

  /**Save/Update Owner Field */
  saveOwnerFieldSource(report: BVQReport, field: string, type: string, note: string, oid: String, column: string, value: any, eid: String, sourceTitle: string): Observable<RestResponse> {
    return this.http
      .put<RestResponse>(`${environment.API_URL}/api/report/${report.ID}/entity/${eid}/owner/${oid}/field?field=${field}&source=${type}&note=` +
        (note ? note : '') + '&sourceTitle=' + (sourceTitle ? sourceTitle : ''), {
        FIELD: column,
        VALUE: value
      }, ApiHeaders.getOptions());
  }

  /**Save/Update Location Field */
  saveLocationFieldSource(report: BVQReport, field: string, type: string, note: string, lid: String, column: string, value: any, sourceTitle: string): Observable<RestResponse> {
    return this.http
      .post<RestResponse>(`${environment.API_URL}/api/bvq/report/${report.ID}/location/${lid}/field?field=${field}&source=${type}&note=` +
        (note ? note : '') + '&sourceTitle=' + (sourceTitle ? sourceTitle : ''), {
        FIELD: column,
        VALUE: value
      }, ApiHeaders.getOptions());
  }

  /**Save/Update Competitor Field */
  saveCompeitorFieldSource(report: BVQReport, field: string, type: string, note: string, cid: String, column: string, value: any, sourceTitle: string): Observable<RestResponse> {
    return this.http
      .post<RestResponse>(`${environment.API_URL}/api/bvq/report/${report.ID}/competitor/${cid}/field?field=${field}&source=${type}&note=` +
        (note ? note : '') + '&sourceTitle=' + (sourceTitle ? sourceTitle : ''), {
        FIELD: column,
        VALUE: value
      }, ApiHeaders.getOptions());
  }

  /**Save/Update Product Field */
  saveProductFieldSource(report: BVQReport, field: string, type: string, note: string, pid: String, column: string, value: any, sourceTitle: string): Observable<RestResponse> {
    return this.http
      .post<RestResponse>(`${environment.API_URL}/api/bvq/report/${report.ID}/product/${pid}/field?field=${field}&source=${type}&note=` +
        (note ? note : '') + '&sourceTitle=' + (sourceTitle ? sourceTitle : ''), {
        FIELD: column,
        VALUE: value
      }, ApiHeaders.getOptions());
  }

  /**Save/Update Customer Field */
  saveCustomerFieldSource(report: BVQReport, field: string, type: string, note: string, cid: String, column: string, value: any, sourceTitle: string): Observable<RestResponse> {
    return this.http
      .post<RestResponse>(`${environment.API_URL}/api/bvq/report/${report.ID}/customer/${cid}/field?field=${field}&source=${type}&note=` +
        (note ? note : '') + '&sourceTitle=' + (sourceTitle ? sourceTitle : ''), {
        FIELD: column,
        VALUE: value
      }, ApiHeaders.getOptions());
  }

  /**Save/Update Contact Field */
  saveContactFieldSource(report: BVQReport, field: string, type: string, note: string, cid: String, column: string, value: any, sourceTitle: string): Observable<RestResponse> {
    return this.http
      .post<RestResponse>(`${environment.API_URL}/api/bvq/report/${report.ID}/contact/${cid}/field?field=${field}&source=${type}&note=` +
        (note ? note : '') + '&sourceTitle=' + (sourceTitle ? sourceTitle : ''), {
        FIELD: column,
        VALUE: value
      }, ApiHeaders.getOptions());
  }

  /**Save/Update Report Field */
  saveReportField(report: BVQReport, field: string, type: string, note: string, value: any, sourceTitle: string): Observable<RestResponse> {
    return this.http
      .post<RestResponse>(`${environment.API_URL}/api/bvq/report/${report.ID}?source=${type}&note=` +
      (note ? note : '') + '&sourceTitle=' + (sourceTitle ? sourceTitle : ''),
        {FIELD: field, VALUE: value}, ApiHeaders.getOptions());
  }

  saveAskContent(id: string, field: string, value: any): Observable<RestResponse> {
    return this.http
      .post<RestResponse>(`${environment.API_URL}/api/bvq/report/${id}`,
        {FIELD: field, VALUE: value}, ApiHeaders.getOptions());
  }

  addEntity(id: string): Observable<RestResponse> {
    return this.http
      .put<RestResponse>(`${environment.API_URL}/api/bvq/report/${id}/entity`, null, ApiHeaders.getOptions());
  }

  savePullData(id: string, data: any): Observable<RestResponse> {
    return this.http
      .post<RestResponse>(`${environment.API_URL}/api/bvq/report/${id}/research`, data, ApiHeaders.getOptions());
  }

  copyOwners(reportId: string, entityId: string): Observable<RestResponse> {
    return this.http
      .post<RestResponse>(`${environment.API_URL}/api/report/${reportId}/entity/${entityId}/owner/copy`, null, ApiHeaders.getOptions());
  }

  saveReportIndustrySource(report: BVQReport, sourceField: string, askField: string, type: string, note: string, value: any): Observable<RestResponse> {
    return this.http
      .post<RestResponse>(`${environment.API_URL}/api/bvq/report/${report.ID}/industry?sourceField=${sourceField}&askField=${askField}&source=${type}&note=` +
        (note ? note : ''), {
          FIELD: "FIELD",
          VALUE: value
        }, ApiHeaders.getOptions());
  }

   /**Save/Update Census Field */
   saveCensus(report: BVQReport, lid: String, value: any): Observable<RestResponse> {
    return this.http
      .post<RestResponse>(`${environment.API_URL}/api/bvq/report/${report.ID}/location/${lid}/field/census`, value, ApiHeaders.getOptions());
  }

  word(id: string, data: any): Observable<any> {
    return this.http
      .put(`${environment.API_URL}/api/bvq/report/${id}/word`, { data }, {responseType: 'blob' as 'blob'})
  }

  /** Custom Question */
  questionCustomUpdate(reportId: string, custom: CustomQuestion, type: string): Observable<RestResponse> {
    return this.http
      .post<RestResponse>(`${environment.API_URL}/api/bvq/report/${reportId}/custom/question?type=${type}`, custom, ApiHeaders.getOptions());
  }

  questionCustomDelete(reportId: string, customId: number): Observable<RestResponse> {
    return this.http
      .post<RestResponse>(`${environment.API_URL}/api/bvq/report/${reportId}/custom/question/${customId}`, null, ApiHeaders.getOptions());
  }

  downloadFile(path: string): any {
    return this.http.get<Blob>(`${environment.API_URL}${path}`, { observe: 'response', responseType: 'blob' as 'json' });
  }

  refreshResearch(id: string): Observable<RestResponse> {
    return this.http
      .get<RestResponse>(`${environment.API_URL}/api/research/content/${id}/refresh`, ApiHeaders.getOptions());
  }

  sendEmail(id: string, field:string): Observable<RestResponse> {
    return this.http
      .post<RestResponse>(`${environment.API_URL}/api/bvq/report/${id}/send-links?toRecipient=${field}`, null, ApiHeaders.getOptions());
  }

  sendFollowUp(reportId: string, token: string): Observable<RestResponse> {
    return this.http
      .post<RestResponse>(`${environment.API_URL}/api/bvq/report/${reportId}/follow-up?token=${token}`, null, ApiHeaders.getOptions());
  }

  disableFollowUp(reportId: string, value: boolean, token: string): Observable<RestResponse> {
    return this.http
      .put<RestResponse>(`${environment.API_URL}/api/bvq/report/${reportId}/updateFollowUp?isDisableFollowUp=${value}&token=${token}`, null, ApiHeaders.getOptions());
  }

   // parseErrorBlob() - Parse blob error.
  private parseErrorBlob(err: HttpErrorResponse): Observable<any> {
    const reader: FileReader = new FileReader();

    const obs = Observable.create((observer: any) => {
      reader.onloadend = (): any => {
        const returnError: any = err;
        returnError.json = JSON.parse(reader.result as any);
        observer.error(returnError);
        observer.complete();
      };
    });
    reader.readAsText(err.error);
    return obs;
  }

}
